import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, ChevronDown } from 'lucide-react';
import { MarketingCard } from '../primitives/MarketingCard';
import { Chip } from '../primitives/Chip';

interface OperationCardProps {
  title: string;
  chips: { label: string; variant: 'default' | 'primary' }[];
  description: string;
  outputs: string[];
  bestFor: string;
}

/**
 * Operation card component for displaying product operation details
 * Features expandable sections for outputs and best use cases
 *
 * @example
 * <OperationCard
 *   title="Validate"
 *   chips={[{ label: 'Batch', variant: 'default' }]}
 *   description="Contact verification and validation"
 *   outputs={['primary_phone', 'primary_email']}
 *   bestFor="Cleaning existing lists"
 * />
 */
export function OperationCard({
  title,
  chips,
  description,
  outputs,
  bestFor
}: OperationCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <MarketingCard className="p-4 h-full flex flex-col">
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-lg font-semibold text-[#0E1325]">{title}</h3>
        <div className="flex gap-2 flex-wrap justify-end">
          {chips.map((chip, i) => (
            <Chip key={i} variant={chip.variant}>
              {chip.label}
            </Chip>
          ))}
        </div>
      </div>

      <p className="text-[15px] text-[#5F6B7A] leading-7 mb-4 flex-grow">{description}</p>

      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="text-sm text-[#2F7BFF] font-medium hover:text-[#2665D9] flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-[#2F7BFF]/40 rounded"
        aria-expanded={isExpanded}
      >
        {isExpanded ? 'Hide details' : 'Show details'}
        <ChevronDown
          className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden mt-4 pt-4 border-t border-[#E6ECF2]"
          >
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-[#0E1325] mb-2">Primary Outputs</h4>
              <ul className="space-y-1.5">
                {outputs.map((output, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#5F6B7A]">
                    <CheckCircle2 className="w-4 h-4 text-[#25C281] flex-shrink-0 mt-0.5" />
                    <span>{output}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#F7F9FC] rounded-lg p-3">
              <p className="text-sm text-[#5F6B7A]">
                <span className="font-semibold text-[#0E1325]">Best for:</span> {bestFor}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </MarketingCard>
  );
}
